<template>
  <section class="admin-page">
    <div class="admin-scroll">
      <header class="admin-header">
        <div>
          <span class="admin-label">用户管理</span>
          <h2>管理用户账号</h2>
          <p class="admin-desc">管理用户账号、角色与权限，维护系统安全。</p>
        </div>
      </header>

      <section class="admin-panel">
        <div class="admin-toolbar">
          <label class="admin-search">
            <svg class="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" class="admin-search-input" placeholder="搜索用户名" v-model="searchKeyword" />
          </label>
        </div>

        <div class="admin-list">
          <div v-if="loading" class="admin-loading">
            <div class="admin-spinner"></div>
            <span>加载中...</span>
          </div>
          <article
            v-for="user in filteredUsers"
            :key="user.id"
            class="admin-list-item"
          >
            <div class="admin-list-body">
              <div class="admin-user-row">
                <h3 class="admin-list-title">{{ user.username }}</h3>
                <span :class="['admin-status', user.role === 2 ? 'admin-status-admin' : 'admin-status-user']">
                  {{ user.role === 2 ? '管理员' : '普通用户' }}
                </span>
                <span :class="['admin-status', user.status === 1 ? 'admin-status-active' : 'admin-status-banned']">
                  {{ user.status === 1 ? '正常' : '封禁' }}
                </span>
              </div>
              <div class="admin-list-meta">
                <span class="admin-meta-tag">{{ formatDate(user.created_at) }}</span>
              </div>
            </div>
            <div class="admin-list-actions">
              <button v-if="user.id === currentAdminId || user.role !== 2" class="admin-btn admin-btn-ghost" type="button" @click="editUser(user)">编辑</button>
              <button v-if="user.id !== currentAdminId && user.role !== 2" class="admin-btn admin-btn-ghost" type="button" style="color: var(--danger);" @click="handleDeleteUser(user.id)">删除</button>
            </div>
          </article>

          <div v-if="filteredUsers.length === 0" class="admin-empty">
            <h3>暂无用户</h3>
            <p>暂无普通用户数据。</p>
          </div>
        </div>

        <AdminPagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :on-page-change="goToPage"
        />
      </section>
    </div>

    <!-- 编辑模态框 -->
    <div v-if="showModal" class="admin-modal-overlay" @wheel.prevent>
      <div :class="['admin-modal', { 'admin-modal-visible': modalVisible }]" @wheel.stop>
        <div class="admin-modal-header">
          <h2>编辑用户</h2>
          <button class="admin-modal-close" type="button" aria-label="关闭" @click="closeModal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="admin-modal-body">
          <!-- 用户头像显示 -->
          <div class="admin-user-avatar-section">
            <div class="admin-user-avatar">
              <img v-if="currentUser?.avatar" :src="currentUser.avatar" alt="用户头像" />
              <div v-else class="admin-user-avatar-placeholder">
                {{ currentUser?.username?.charAt(0)?.toUpperCase() || '?' }}
              </div>
            </div>
            <div class="admin-user-info">
              <div class="admin-user-id">ID: {{ currentUser?.id }}</div>
              <div class="admin-user-created">注册时间: {{ formatDate(currentUser?.created_at) }}</div>
            </div>
          </div>

          <div class="admin-field">
            <label class="admin-field-label required" for="username">用户名</label>
            <input id="username" v-model.trim="formData.username" class="admin-input" type="text" placeholder="请输入用户名" />
            <div v-if="errors.username" class="admin-field-error">{{ errors.username }}</div>
          </div>

          <div class="admin-field">
            <label class="admin-field-label required" for="role">用户角色</label>
            <select id="role" v-model="formData.role" class="admin-input admin-select" :disabled="currentUser?.id === currentAdminId">
              <option :value="1">普通用户</option>
              <option :value="2">作者</option>
            </select>
            <div v-if="currentUser?.id === currentAdminId" class="admin-field-hint">不能修改自己的角色</div>
          </div>

          <div class="admin-field">
            <label class="admin-field-label required" for="status">用户状态</label>
            <select id="status" v-model="formData.status" class="admin-input admin-select" :disabled="currentUser?.id === currentAdminId">
              <option :value="1">正常</option>
              <option :value="2">封禁</option>
            </select>
            <div v-if="currentUser?.id === currentAdminId" class="admin-field-hint">不能修改自己的状态</div>
          </div>

          <template v-if="currentUser?.id === currentAdminId">
            <div class="admin-field">
              <label class="admin-field-label" for="password">新密码（留空则不修改）</label>
              <input id="password" v-model.trim="formData.password" class="admin-input" type="password" placeholder="请输入新密码" />
              <div v-if="errors.password" class="admin-field-error">{{ errors.password }}</div>
            </div>

            <div class="admin-field">
              <label class="admin-field-label" for="confirmPassword">确认密码</label>
              <input id="confirmPassword" v-model.trim="formData.confirmPassword" class="admin-input" type="password" placeholder="请再次输入新密码" />
              <div v-if="errors.confirmPassword" class="admin-field-error">{{ errors.confirmPassword }}</div>
            </div>
          </template>
        </div>

        <div class="admin-modal-footer">
          <button class="admin-btn admin-btn-secondary" type="button" @click="closeModal">取消</button>
          <button class="admin-btn admin-btn-primary" type="button" :disabled="isSubmitting" @click="saveUser">
            <span v-if="isSubmitting" class="admin-spinner"></span>
            {{ isSubmitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getAdminUsers, updateUser, deleteUser as deleteUserApi } from '@/services/userService'
import { useNotify } from '@/composables/useNotify'
import { useUserStore } from '@/stores/user'
import { stopTokenRefreshTimer } from '@/services/authService'
import AdminPagination from '../components/admin/AdminPagination.vue'

const router = useRouter()
const { notify, confirmDialog } = useNotify()
const userStore = useUserStore()
const currentAdminId = computed(() => userStore.userInfo?.id)

const users = ref([])
const loading = ref(true)
const searchKeyword = ref('')
const showModal = ref(false)
const modalVisible = ref(false)
const currentUserId = ref(null)
const currentUser = ref(null)
const isSubmitting = ref(false)
const currentPage = ref(1)
const totalPages = ref(1)
const totalUsers = ref(0)
const PER_PAGE = 10

watch(searchKeyword, () => {
  currentPage.value = 1
  fetchUsers()
})

const formData = ref({
  username: '',
  password: '',
  confirmPassword: '',
  role: 1,
  status: 1
})

const errors = ref({
  username: '',
  password: '',
  confirmPassword: ''
})

const filteredUsers = computed(() => users.value)

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

const fetchUsers = async () => {
  loading.value = true
  try {
    const keyword = searchKeyword.value.trim()
    const res = await getAdminUsers(currentPage.value, PER_PAGE, keyword)
    const data = res.data
    users.value = data?.list || []
    totalUsers.value = data?.total || 0
    totalPages.value = data?.total_page || Math.ceil(totalUsers.value / PER_PAGE) || 1
  } catch (e) {
    notify.error(e.message || '获取用户列表失败')
  } finally {
    loading.value = false
  }
}

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchUsers()
}

const editUser = (user) => {
  currentUserId.value = user.id
  currentUser.value = user
  formData.value = {
    username: user.username,
    password: '',
    confirmPassword: '',
    role: user.role,
    status: user.status
  }
  errors.value = {
    username: '',
    password: '',
    confirmPassword: ''
  }
  showModal.value = true
  requestAnimationFrame(() => { modalVisible.value = true })
}

const closeModal = () => {
  modalVisible.value = false
  setTimeout(() => { showModal.value = false }, 250)
}

const validateForm = () => {
  let isValid = true
  errors.value = {
    username: '',
    password: '',
    confirmPassword: ''
  }

  if (!formData.value.username) {
    errors.value.username = '请输入用户名'
    isValid = false
  } else if (formData.value.username.length < 3 || formData.value.username.length > 50) {
    errors.value.username = '用户名长度应在3-50个字符之间'
    isValid = false
  }

  if (formData.value.password) {
    if (formData.value.password.length < 6 || formData.value.password.length > 50) {
      errors.value.password = '密码长度应在6-50个字符之间'
      isValid = false
    }

    if (formData.value.password !== formData.value.confirmPassword) {
      errors.value.confirmPassword = '两次输入的密码不一致'
      isValid = false
    }
  }

  return isValid
}

const saveUser = async () => {
  if (!validateForm()) return

  // 判断是否修改了自己的密码
  const isSelfPasswordChange = currentUser.value?.id === currentAdminId.value && formData.value.password

  isSubmitting.value = true
  try {
    const updateData = {
      username: formData.value.username
    }

    // 如果不是修改自己，可以修改角色和状态
    if (currentUser.value?.id !== currentAdminId.value) {
      updateData.role = formData.value.role
      updateData.status = formData.value.status
    }

    if (formData.value.password) {
      updateData.password = formData.value.password
    }
    await updateUser(currentUserId.value, updateData)

    // 修改了自己的密码，提示并跳转登录
    if (isSelfPasswordChange) {
      closeModal()
      notify.warning('身份信息已变更，请重新登录')
      userStore.logout()
      stopTokenRefreshTimer()
      setTimeout(() => { router.push('/login') }, 2000)
      return
    }

    await fetchUsers()
    closeModal()
    notify.success('用户信息修改成功')
  } catch (e) {
    notify.error(`保存失败：${e.message}`)
  } finally {
    isSubmitting.value = false
  }
}

const handleDeleteUser = async (id) => {
  if (!await confirmDialog('确定要删除这个用户吗？')) return
  try {
    await deleteUserApi(id)
    await fetchUsers()
    notify.success('用户删除成功')
  } catch (e) {
    notify.error(`删除失败：${e.message}`)
  }
}

// 模态框滚动锁定
watch(showModal, (visible) => {
  if (visible) {
    const bodyScrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (bodyScrollbarWidth > 0) {
      document.body.style.paddingRight = `${bodyScrollbarWidth}px`
    }
  } else {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
})

onMounted(() => {
  fetchUsers()
})
</script>

<style scoped>
/* 样式与 AdminArticleManageView.vue 类似 */

.admin-modal {
  width: min(480px, 96vw);
  min-width: unset;
  min-height: unset;
}

.admin-user-avatar-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.admin-user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.admin-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.admin-user-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.admin-user-info {
  flex: 1;
}

.admin-user-id {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 2px;
}

.admin-user-created {
  font-size: 13px;
  color: var(--text-secondary);
}

.admin-user-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.admin-status-admin {
  background: rgba(192, 57, 43, 0.1);
  color: #c0392b;
  border: 1px solid rgba(192, 57, 43, 0.2);
}

.admin-status-user {
  background: rgba(102, 102, 102, 0.1);
  color: #666666;
  border: 1px solid rgba(102, 102, 102, 0.2);
}

.admin-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}

.admin-field-error {
  color: var(--danger);
  font-size: 12px;
  margin-top: 4px;
}

.admin-field-hint {
  color: var(--text-secondary);
  font-size: 12px;
  margin-top: 4px;
}

.admin-status-active {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.admin-status-banned {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}
</style>
